/**
 * Cache layer using Telegram CloudStorage
 * 
 * CloudStorage limits:
 * - Key length: 1-128 characters
 * - Value length: 0-4096 characters
 * - Up to 1024 keys
 * 
 * We use this to persist menu data between sessions, reducing API calls.
 */

import WebApp from '@twa-dev/sdk';
import { logger } from './logger';

const log = logger.withContext({ component: 'cache' });

// Cache version - bump this to invalidate all cached data
const CACHE_VERSION = 1;

interface CacheEntry<T> {
  version: number;
  data: T;
  timestamp: number;
  ttl: number; // milliseconds
}

/**
 * Default TTL values (in milliseconds)
 */
export const CACHE_TTL = {
  MENU: 2 * 60 * 1000, // 2 minutes - balance between performance and freshness
} as const;

/**
 * Check if CloudStorage is available
 */
function isCloudStorageAvailable(): boolean {
  return typeof WebApp.CloudStorage?.setItem === 'function';
}

/**
 * Get item from cache
 */
export async function getCacheItem<T>(key: string): Promise<T | null> {
  if (!isCloudStorageAvailable()) {
    log.debug('CloudStorage not available');
    return null;
  }

  try {
    const value = await new Promise<string | null>((resolve) => {
      WebApp.CloudStorage.getItem(key, (error, value) => {
        if (error) {
          log.warn('CloudStorage.getItem error', { key, error });
          resolve(null);
        } else {
          resolve(value || null);
        }
      });
    });

    if (!value) return null;

    const entry: CacheEntry<T> = JSON.parse(value);

    // Check cache version
    if (entry.version !== CACHE_VERSION) {
      log.debug('Cache version mismatch, invalidating', { key, expected: CACHE_VERSION, got: entry.version });
      await removeCacheItem(key);
      return null;
    }

    // Check if expired
    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      log.debug('Cache expired', { key, age: now - entry.timestamp, ttl: entry.ttl });
      await removeCacheItem(key);
      return null;
    }

    log.debug('Cache hit', { key, age: now - entry.timestamp });
    return entry.data;
  } catch (error) {
    log.warn('Error reading from cache', { key, error });
    return null;
  }
}

/**
 * Set item in cache
 */
export async function setCacheItem<T>(key: string, data: T, ttl: number): Promise<boolean> {
  if (!isCloudStorageAvailable()) {
    return false;
  }

  try {
    const entry: CacheEntry<T> = {
      version: CACHE_VERSION,
      data,
      timestamp: Date.now(),
      ttl,
    };

    const value = JSON.stringify(entry);

    // Check value size limit (4096 bytes)
    if (value.length > 4096) {
      log.warn('Cache value too large, skipping', { key, size: value.length });
      return false;
    }

    await new Promise<void>((resolve, reject) => {
      WebApp.CloudStorage.setItem(key, value, (error, success) => {
        if (error || !success) {
          reject(error || new Error('CloudStorage.setItem failed'));
        } else {
          resolve();
        }
      });
    });

    log.debug('Cache set', { key, size: value.length, ttl });
    return true;
  } catch (error) {
    log.warn('Error writing to cache', { key, error });
    return false;
  }
}

/**
 * Remove item from cache
 */
export async function removeCacheItem(key: string): Promise<void> {
  if (!isCloudStorageAvailable()) return;

  try {
    await new Promise<void>((resolve) => {
      WebApp.CloudStorage.removeItem(key, () => resolve());
    });
  } catch {
    // Ignore errors on remove
  }
}

/**
 * Generate cache key for menu
 */
export function getMenuCacheKey(restaurantId: string): string {
  return `menu_${restaurantId}`;
}

/**
 * Invalidate menu cache for a restaurant
 * Call this after any menu modification (create/update/delete items, categories, modifiers)
 */
export async function invalidateMenuCache(restaurantId: string): Promise<void> {
  const key = getMenuCacheKey(restaurantId);
  await removeCacheItem(key);
  log.debug('Menu cache invalidated', { restaurantId });
}
